import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "../utils/convex-api";
import { View, Text } from "react-native";
import { Select } from "../ui/select";
import { useSheet } from "./sheet-provider";
import { SheetIds } from "../utils/sheet-ids";
import {
  SingleSelectSheetPayload,
  SingleSelectSheetResult,
} from "../sheets/single-select-sheet";
import {
  BufferTimeConflictSheetPayload,
  BufferTimeConflictSheetResult,
} from "../sheets/buffer-time-conflict-sheet";
import { format, addMinutes, differenceInMinutes, subMinutes } from "date-fns";
import { Id } from "@teachloop/backend/_generated/dataModel";

type BufferTimeSettingsProps = {
  beforeLessonBuffer: number;
  afterLessonBuffer: number;
  setBeforeLessonBuffer: (buffer: number) => void;
  setAfterLessonBuffer: (buffer: number) => void;
  originalStartTime: number;
  originalEndTime: number;
  bookingId: Id<"bookings">;
};

const BufferTimeOptions = [
  { label: "15 minutes", value: 15 },
  { label: "30 minutes", value: 30 },
  { label: "45 minutes", value: 45 },
  { label: "60 minutes", value: 60 },
];

function BufferTimeSettings({
  beforeLessonBuffer,
  afterLessonBuffer,
  setBeforeLessonBuffer,
  setAfterLessonBuffer,
  originalStartTime,
  originalEndTime,
  bookingId,
}: BufferTimeSettingsProps) {
  const { open: openBeforeLessonBufferSelect } = useSheet<
    SingleSelectSheetPayload,
    SingleSelectSheetResult
  >(SheetIds.SINGLE_SELECT);
  const { open: openAfterLessonBufferSelect } = useSheet<
    SingleSelectSheetPayload,
    SingleSelectSheetResult
  >(SheetIds.SINGLE_SELECT);
  const { open: openBufferConflictSheet } = useSheet<
    BufferTimeConflictSheetPayload,
    BufferTimeConflictSheetResult
  >(SheetIds.BUFFER_TIME_CONFLICT);

  const originalLessonDuration = useMemo(() => {
    return differenceInMinutes(new Date(originalEndTime), new Date(originalStartTime));
  }, [originalStartTime, originalEndTime]);

  const startTime = useMemo(() => {
    return subMinutes(new Date(originalStartTime), beforeLessonBuffer);
  }, [originalStartTime, beforeLessonBuffer]);

  const endTime = useMemo(() => {
    return addMinutes(new Date(originalEndTime), afterLessonBuffer);
  }, [originalEndTime, afterLessonBuffer]);

  const handleOpenBeforeLessonBufferSelect = async () => {
   const result = await openBeforeLessonBufferSelect({
      title: "Select buffer time before lesson",
      options: BufferTimeOptions,
      preSelectedOption: beforeLessonBuffer,
      uiMode: "radio",
    });
    if (result.selectedOption) {
      setBeforeLessonBuffer(result.selectedOption as number);
      await checkForConflicts();
    }
  };

  const handleOpenAfterLessonBufferSelect = async () => {
    const result = await openAfterLessonBufferSelect({
      title: "Select buffer time after lesson",
      options: BufferTimeOptions,
      preSelectedOption: afterLessonBuffer,
      uiMode: "radio",
    });
    if (result.selectedOption) {
      setAfterLessonBuffer(result.selectedOption as number);
      await checkForConflicts();
    }
  };

  const { data: conflicts = [] } = useQuery({
    ...convexQuery(api.tutor.reviewLesson.queries.getBufferConflicts, {
      bookingId,
      bufferStartTime: startTime.getTime(),
      bufferEndTime: endTime.getTime(),
    }),
    enabled: !!bookingId,
  });

  const checkForConflicts = async () => {
    if (conflicts.length > 0) {
      const formattedConflicts = conflicts.map(conflict => ({
        ...conflict,
        student: {
          ...conflict.student,
          imageUrl: conflict.student.imageUrl || undefined,
        },
      }));
      
      await openBufferConflictSheet({
        conflicts: formattedConflicts,
      });
    }
  };

  return (
    <View className="gap-6">
      <View className="gap-1">
        <Text className="font-geologica-medium font-medium text-xl leading-[1.5] text-gray-800">
          Buffer time settings
        </Text>
        <Text className="font-geologica-regular font-normal text-base leading-[1.5] text-gray-800">
          Add buffer time before and after lessons for travel and prep.
        </Text>
      </View>
      <View className="gap-4">
        <View className="flex-row justify-between items-center gap-4">
          <View className="gap-1 flex-1">
            <Text className="font-geologica-regular font-normal text-sm leading-[1.5] text-dark">
              Buffer before lesson
            </Text>
            <Select
              className="py-[11]"
              onPress={handleOpenBeforeLessonBufferSelect}
            >
              <Select.Label>
                {
                  BufferTimeOptions.find(
                    (option) => option.value === beforeLessonBuffer
                  )?.label
                }
              </Select.Label>
              <Select.DownArrow />
            </Select>
          </View>
          <View className="gap-1 flex-1">
            <Text className="font-geologica-regular font-normal text-sm leading-[1.5] text-dark">
              Buffer after lesson
            </Text>
            <Select
              className="py-[11]"
              onPress={handleOpenAfterLessonBufferSelect}
            >
              <Select.Label>
                {
                  BufferTimeOptions.find(
                    (option) => option.value === afterLessonBuffer
                  )?.label
                }
              </Select.Label>
              <Select.DownArrow />
            </Select>
          </View>
        </View>
        <View className="bg-teachLoop-creamDark rounded-[16] pt-6 px-4 pb-[30] gap-6">
          <View className="flex-row justify-between gap-[26]">
            <View>
              <Text className="font-geologica-regular font-normal text-sm leading-[1.5] text-black">
                Buffer before
              </Text>
              <Text className="font-geologica-medium font-medium text-base leading-[1.5] text-black">
                {beforeLessonBuffer} mins
              </Text>
            </View>
            <View>
              <Text className="font-geologica-regular font-normal text-sm leading-[1.5] text-black">
                Lesson
              </Text>
              <Text className="font-geologica-medium font-medium text-base leading-[1.5] text-black">
                {originalLessonDuration} mins
              </Text>
            </View>
            <View>
              <Text className="font-geologica-regular font-normal text-sm leading-[1.5] text-black">
                Buffer after
              </Text>
              <Text className="font-geologica-medium font-medium text-base leading-[1.5] text-black">
                {afterLessonBuffer} mins
              </Text>
            </View>
          </View>
          <View className="gap-1">
            <Text className="text-center font-geologica-regular font-normal text-sm leading-[1.5] text-black">
              Calendar block from {format(startTime, "HH:mm")} to{" "}
              {format(endTime, "HH:mm")}
            </Text>
            <Text className="text-center font-geologica-medium font-medium text-base leading-[1.5] text-black">
              Total duration: {differenceInMinutes(endTime, startTime)} mins
            </Text>
          </View>
        </View>
      </View>
    </View>
  );
}

export default BufferTimeSettings;
